import { FilterParameters } from "_shared/_api/queries/filtering/filter-parameters";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import FeeScheduleTemplateFilterSchema, {
  FeeScheduleTemplateFilterJSON,
  FeeScheduleTemplateFilterParams,
} from "work/fee-schedule-templates/_api/queries/contracts/filtering/_schema/fee-schedule-template-filter-schema";

export class FeeScheduleTemplateFilterParameters extends FilterParameters {
  public static readonly defaultOptions = {
    queryParamName: "query",
    entityIdParamName: "entityId",
    creatorUserIdParamName: "creatorUserId",
    nameParamName: "name",
    minCreatedDateParamName: "minCreatedDate",
    maxCreatedDateParamName: "maxCreatedDate",
    tagsParamName: "tags",
    isArchivedParamName: "isArchived",
    isActiveParamName: "isActive"
  }
  public static fromJSON(json: FeeScheduleTemplateFilterJSON): FeeScheduleTemplateFilterParameters {
    const params = FeeScheduleTemplateFilterSchema.deserialize(json);
    return new FeeScheduleTemplateFilterParameters(params);
  }

  public static fromSearchParams(args: {
    searchParams: URLSearchParams,
    session: Session,
    options?: {
      queryParamName?: string;
      entityIdParamName?: string;
      creatorUserIdParamName?: string;
      nameParamName?: string;
      minCreatedDateParamName?: string;
      maxCreatedDateParamName?: string;
      tagsParamName?: string;
      isArchivedParamName?: string;
      isActiveParamName?: string;
    };
  }): FeeScheduleTemplateFilterParameters {
    const { searchParams, session, options } = args;
    const paramsNames = { ...FeeScheduleTemplateFilterParameters.defaultOptions, ...options };
    const entityId =
      Guid.fromJSON(searchParams.get(paramsNames.entityIdParamName)) ?? session.user!.legalEntityId;

    const tags = searchParams.getAll(paramsNames.tagsParamName);
    const isArchivedRaw = searchParams.get(paramsNames.isArchivedParamName);
    const isActiveRaw = searchParams.get(paramsNames.isActiveParamName);

    return new FeeScheduleTemplateFilterParameters({
      query: searchParams.get(paramsNames.queryParamName) ?? undefined,
      entityId,
      creatorUserId: Guid.fromJSON(searchParams.get(paramsNames.creatorUserIdParamName)) ?? undefined,
      name: searchParams.get(paramsNames.nameParamName) ?? undefined,
      minCreatedDate: searchParams.get(paramsNames.minCreatedDateParamName)
        ? new AHDate(searchParams.get(paramsNames.minCreatedDateParamName)!)
        : undefined,
      maxCreatedDate: searchParams.get(paramsNames.maxCreatedDateParamName)
        ? new AHDate(searchParams.get(paramsNames.maxCreatedDateParamName)!)
        : undefined,
      tags: tags.length ? tags : undefined,
      isArchived:
        isArchivedRaw === null ? undefined : new AHBoolean(isArchivedRaw === "true"),
      isActive:
        isActiveRaw === null ? undefined : new AHBoolean(isActiveRaw === "true"),
    });
  }

  public query?: string;
  public entityId: Guid;
  public creatorUserId?: Guid;
  public name?: string;
  public minCreatedDate?: AHDate;
  public maxCreatedDate?: AHDate;
  public tags?: string[];
  public isArchived?: AHBoolean;
  public isActive?: AHBoolean;

  constructor(params: FeeScheduleTemplateFilterParams) {
    super();
    Object.assign(this, params);
    this.entityId = params.entityId;
  }

  get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.query) searchParams.append("query", this.query);
    if (this.entityId) searchParams.append("entityId", this.entityId.toJSON());
    if (this.creatorUserId) searchParams.append("creatorUserId", this.creatorUserId.toJSON());
    if (this.name) searchParams.append("name", this.name);
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
    if (this.tags) {
      for (const tag of this.tags) {
        searchParams.append("tags", tag);
      }
    }
    if (this.isArchived !== undefined) searchParams.append("isArchived", String(this.isArchived));
    if (this.isActive !== undefined) searchParams.append("isActive", String(this.isActive));
    return searchParams;
  }

  public isEqualTo(other: FeeScheduleTemplateFilterParameters): boolean {
    if (!other || !(other instanceof FeeScheduleTemplateFilterParameters)) return false;
    return Boolean(
      this.query === other.query &&
      this.entityId === other.entityId &&
      this.creatorUserId === other.creatorUserId &&
      this.name === other.name &&
      this.minCreatedDate?.isEqualTo(other.minCreatedDate) &&
      this.maxCreatedDate?.isEqualTo(other.maxCreatedDate) &&
      JSON.stringify(this.tags) === JSON.stringify(other.tags) &&
      this.isArchived === other.isArchived &&
      this.isActive === other.isActive
    );
  }
}
